/**
 * 场景级故事板生成 Panel · N 格并排黑白线稿分镜
 *
 * 图(画面)由 Seedream 出,中文标注(镜号/景别/运镜/台词/音效/站位)在前端解析 cameraPrompt 后叠加渲染。
 * 数据:scene.storyboardRuns / scene.selectedStoryboardImageUrl
 * 任务态:本地 pollTaskId + GET /director/tasks/{id} 5s 轮询
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clapperboard, Loader2, Play, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { InlineLightbox } from '@/pages/brain/Lightbox';
import type {
  DirectorSceneShots,
  DirectorTaskSnapshot,
  SceneStoryboardRun,
  StoryboardGenerateResponse,
  StoryboardModel,
  StoryboardSelectResponse,
} from './types';

const MODEL_LABEL: Record<StoryboardModel, string> = {
  seedream: 'Seedream',
  'gpt-image': 'GPT-Image',
};
const MODEL_OPTIONS: StoryboardModel[] = ['gpt-image', 'seedream'];

interface Props {
  episodeId: string;
  episodeIndex: number;
  sceneId: string;
  scene: DirectorSceneShots;
  aspectRatio: '9:16' | '16:9';
  prevEndingFrameUrl?: string | null;
  usePrevEndingFrame: boolean;
}

interface ShotLabel {
  no: string;
  shotSize: string;
  cameraMove: string;
  duration: string;
  dialogue: string;
  sfx: string;
  staging: string;
}

const RUNNING = (s?: string | null) => s === 'queued' || s === 'running';

// 解析 cameraPrompt 的逐镜块。现有生成器输出「镜头1（2.0s）——主题」+ 效果/画面/动作/剧情对白/运镜说明 字段;
// 同时兼容用户模板的「镜1 | 景别 | 运镜 | 时长秒」+ 台词/音效/角色站位 字段。
function parseShotLabels(cameraPrompt: string | null | undefined): ShotLabel[] {
  const text = (cameraPrompt ?? '').trim();
  if (!text) return [];
  const heads: { no: string; index: number }[] = [];
  for (const headRe of [
    /^\s*(?:#{1,6}\s*)?(?:[-*]\s*)?(?:\*\*)?\s*镜头\s*(\d+)\b/gm,
    /^\s*(?:#{1,6}\s*)?(?:[-*]\s*)?(?:\*\*)?\s*镜(?!头)\s*(\d+)\b/gm,
  ]) {
    let m: RegExpExecArray | null;
    while ((m = headRe.exec(text)) !== null) {
      heads.push({ no: m[1], index: m.index });
    }
    if (heads.length > 0) break;
  }
  if (heads.length === 0) return [];

  const labels: ShotLabel[] = [];
  for (let i = 0; i < heads.length; i++) {
    const start = heads[i].index;
    const end = i + 1 < heads.length ? heads[i + 1].index : text.length;
    const block = text.slice(start, end);
    const picture = pickField(block, ['画面']);
    labels.push({
      no: heads[i].no,
      shotSize: pickField(block, ['景别']) || shortText(picture, 14),
      cameraMove: pickField(block, ['运镜说明', '效果', '运镜', '运镜/机位']),
      duration: pickDuration(block),
      dialogue: pickField(block, ['剧情/对白', '台词', '对白']),
      sfx: pickField(block, ['音效']),
      staging: pickField(block, ['角色站位示意图', '角色站位', '站位']),
    });
  }
  return labels;
}

function shortText(s: string, max: number): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

// 时长在镜头标题的括号内,如「镜头1（2.0s）」或「镜1 | ... | 3秒」。
function pickDuration(block: string): string {
  const firstLine = block.split('\n')[0] ?? '';
  const paren = firstLine.match(/[（(]\s*(?:约\s*)?(\d+(?:\.\d+)?)\s*[sS秒]?\s*[）)]/);
  if (paren) return `${paren[1]}s`;
  const cn = firstLine.match(/(\d+(?:\.\d+)?)\s*秒/);
  return cn ? `${cn[1]}s` : '';
}

// 取「字段名：内容」直到下一个换行的内容,支持多个候选字段名。
function pickField(block: string, names: string[]): string {
  for (const name of names) {
    const re = new RegExp(`${name}\\s*[：:]\\s*([^\\n]+)`);
    const hit = block.match(re);
    if (hit) return hit[1].replace(/^[-\s]+/, '').trim();
  }
  return '';
}

export function SceneStoryboardPanel({
  episodeId,
  episodeIndex,
  sceneId,
  scene,
  aspectRatio,
  prevEndingFrameUrl,
  usePrevEndingFrame,
}: Props) {
  const qc = useQueryClient();
  const completedTaskIds = useRef<Set<string>>(new Set());

  const runCount = (scene.storyboardRuns ?? []).filter((r) => !!r.url).length;
  // 本轮生成开始时的出图基线;新图数量超过它即视为本轮已出图。
  const baselineRunCount = useRef<number>(runCount);

  const [pollTaskId, setPollTaskId] = useState<string | null>(
    RUNNING(scene.lastStoryboardTaskStatus) ? scene.lastStoryboardTaskId ?? null : null,
  );

  const sceneStoryboardTaskRunning =
    RUNNING(scene.lastStoryboardTaskStatus) &&
    !!scene.lastStoryboardTaskId &&
    !completedTaskIds.current.has(scene.lastStoryboardTaskId);

  const awaitingNewRun = runCount <= baselineRunCount.current;

  useEffect(() => {
    if (
      sceneStoryboardTaskRunning &&
      scene.lastStoryboardTaskId &&
      !pollTaskId &&
      awaitingNewRun
    ) {
      setPollTaskId(scene.lastStoryboardTaskId);
    }
    if (pollTaskId && !awaitingNewRun) {
      setPollTaskId(null);
    }
  }, [sceneStoryboardTaskRunning, scene.lastStoryboardTaskId, runCount, awaitingNewRun, pollTaskId]);

  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  useEffect(() => {
    if (!armedDeleteId) return;
    const t = setTimeout(() => setArmedDeleteId(null), 3000);
    return () => clearTimeout(t);
  }, [armedDeleteId]);

  const basePath = `director/episodes/${episodeId}/director/episodes/${episodeIndex}/scenes/${sceneId}/storyboard`;

  const rawShotLabels = useMemo(() => parseShotLabels(scene.cameraPrompt), [scene.cameraPrompt]);
  const shotLabels = useMemo(
    () => (usePrevEndingFrame ? rawShotLabels : rawShotLabels.filter((lb) => lb.no !== '0')),
    [rawShotLabels, usePrevEndingFrame],
  );
  const shotPanelCount = shotLabels.length;
  const hasInlineContinuityShot = usePrevEndingFrame && shotLabels.some((lb) => lb.no === '0');
  const continuityPanelEnabled = Boolean(prevEndingFrameUrl && usePrevEndingFrame && !hasInlineContinuityShot);
  const panelCount = shotPanelCount + (continuityPanelEnabled ? 1 : 0);
  const narrativeShotCount = hasInlineContinuityShot ? Math.max(0, shotPanelCount - 1) : shotPanelCount;

  const generateMut = useMutation({
    mutationFn: async () =>
      api
        .post(`${basePath}/generate`, { json: { count: 1, model, usePrevEndingFrame } })
        .json<StoryboardGenerateResponse>(),
    onSuccess: (res) => {
      baselineRunCount.current = (scene.storyboardRuns ?? []).filter((r) => !!r.url).length;
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setPollTaskId(res.taskId);
      toast.success('已下发故事板任务', `${res.panelCount} 列 · ${MODEL_LABEL[res.model] ?? res.model}`);
    },
    onError: (e: Error) => toast.error('故事板生成失败', e.message || '请重试'),
  });

  const taskQuery = useQuery({
    queryKey: ['director', 'task', pollTaskId],
    queryFn: () => api.get(`director/tasks/${pollTaskId}`).json<DirectorTaskSnapshot>(),
    enabled: !!pollTaskId,
    refetchInterval: (q) => {
      const s = (q.state.data as DirectorTaskSnapshot | undefined)?.status;
      if (!s) return 5000;
      return RUNNING(s) ? 5000 : false;
    },
  });

  useEffect(() => {
    if (!pollTaskId) return;
    const s = taskQuery.data?.status;
    if (s && !RUNNING(s)) {
      completedTaskIds.current.add(pollTaskId);
      setPollTaskId(null);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      if (s === 'failed') {
        toast.error('故事板生成失败', taskQuery.data?.error || '请重试');
      }
    }
  }, [taskQuery.data?.status, taskQuery.data?.error, pollTaskId, qc, episodeId]);

  const selectMut = useMutation({
    mutationFn: async (runId: string) =>
      api.post(`${basePath}/select`, { json: { runId } }).json<StoryboardSelectResponse>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已选定该故事板');
    },
    onError: (e: Error) => toast.error('选定失败', e.message || '请重试'),
  });

  const deselectMut = useMutation({
    mutationFn: async () => api.post(`${basePath}/deselect`).json<{ sceneId: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已取消选定');
    },
    onError: (e: Error) => toast.error('取消失败', e.message || '请重试'),
  });

  const deleteMut = useMutation({
    mutationFn: async (runId: string) =>
      api.delete(`${basePath}/${runId}`).json<{ sceneId: string; deletedRunId: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已删除该故事板');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const runs = [...(scene.storyboardRuns ?? [])].reverse();
  const hasCameraPrompt = !!scene.cameraPrompt?.trim();
  const taskInFlight =
    generateMut.isPending || !!pollTaskId || sceneStoryboardTaskRunning;
  const showPending = taskInFlight && awaitingNewRun;
  const busy = taskInFlight;
  const canGenerate = hasCameraPrompt && shotPanelCount > 0;
  const hasUnparseableCameraPrompt = hasCameraPrompt && shotPanelCount === 0;

  const [lightbox, setLightbox] = useState<string | null>(null);
  const [model, setModel] = useState<StoryboardModel>('gpt-image');

  return (
    <section className="border-t border-outline-variant/20 bg-neutral-900/[0.02] px-3 py-2.5">
      <header className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          <Clapperboard className="h-3 w-3 text-brand-500" /> 故事板生成
          {panelCount > 0 && (
            <span className="rounded-full bg-neutral-100 px-1.5 py-px text-[10px] font-normal text-neutral-500">
              {panelCount} 格
            </span>
          )}
        </h4>
        <div className="flex items-center gap-1.5">
          <div className="flex overflow-hidden rounded-md border border-outline-variant/40 text-[10px]">
            {MODEL_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setModel(m)}
                disabled={busy}
                className={`px-2 py-1 transition ${
                  model === m
                    ? 'bg-brand-500 text-white'
                    : 'bg-surface text-neutral-500 hover:bg-brand-50'
                } ${busy ? 'cursor-not-allowed opacity-60' : ''}`}
                title={`使用 ${MODEL_LABEL[m]} 模型生成`}
              >
                {MODEL_LABEL[m]}
              </button>
            ))}
          </div>
          <Button
            variant="agent"
            agentColor="director"
            size="sm"
            onClick={() => generateMut.mutate()}
            disabled={!canGenerate || busy}
            title={!hasCameraPrompt ? '请先生成分镜镜头提示词' : hasUnparseableCameraPrompt ? '未识别到镜头标题' : undefined}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            生成故事板
          </Button>
        </div>
      </header>

      {!hasCameraPrompt ? (
        <p className="mb-2 rounded border border-dashed border-amber-200 bg-amber-50/40 px-2 py-1 text-[11px] text-amber-700">
          请先生成分镜镜头提示词
        </p>
      ) : hasUnparseableCameraPrompt ? (
        <p className="mb-2 rounded border border-dashed border-amber-200 bg-amber-50/40 px-2 py-1 text-[11px] text-amber-700">
          未识别到标准镜头标题。请在分镜镜头提示词中使用行首「镜头1（...）」或「镜1 ...」格式后再生成故事板。
        </p>
      ) : (
        <p className="mb-2 rounded border border-dashed border-sky-200 bg-sky-50/40 px-2 py-1 text-[11px] leading-snug text-sky-700">
          根据分镜镜头提示词拆解的 {hasInlineContinuityShot ? `镜头0 + ${narrativeShotCount} 个剧情镜头` : `${shotPanelCount} 个镜头`} + @ 提及的设定图
          {continuityPanelEnabled ? ` + 1 个上一场结束帧衔接格,生成 ${panelCount}` : `,生成 ${panelCount}`}{' '}
          列并排黑白线稿分镜(每列一幅关键帧 + 下方简化正视站位图);镜号/景别/运镜标题与台词/音效由系统叠加在每列上下方
          {prevEndingFrameUrl && (usePrevEndingFrame ? (hasInlineContinuityShot ? '。已开启衔接上镜:第 1 列对应分镜里的镜头0,后续列进入本场剧情。' : '。已开启衔接上镜:第 1 列是上一场结束帧衔接格,第 2 列开始才是本场镜头。') : '。已关闭衔接上镜:本轮不参考上一场结束帧。')}
        </p>
      )}

      {runs.length === 0 && !showPending ? (
        <p className="rounded border border-dashed border-outline-variant/40 px-3 py-4 text-center text-[11px] text-neutral-400">
          尚未生成故事板
        </p>
      ) : (
        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-0.5">
          {showPending && (
            <StoryboardPendingCard
              panelCount={panelCount}
              aspectRatio={aspectRatio}
              hasContinuityPanel={continuityPanelEnabled}
            />
          )}
          {runs.map((r) => (
            <StoryboardCard
              key={r.id}
              run={r}
              labels={shotLabels}
              hasContinuityPanel={Boolean(r.panelCount && r.panelCount > shotLabels.length)}
              aspectRatio={aspectRatio}
              selected={scene.selectedStoryboardImageUrl === r.url && !!r.url}
              onOpen={() => r.url && setLightbox(r.url)}
              onSelect={() => r.url && selectMut.mutate(r.id)}
              onDeselect={() => deselectMut.mutate()}
              disabled={selectMut.isPending}
              armed={armedDeleteId === r.id}
              onArmDelete={() => setArmedDeleteId(r.id)}
              onConfirmDelete={() => {
                setArmedDeleteId(null);
                deleteMut.mutate(r.id);
              }}
              deleting={deleteMut.isPending && deleteMut.variables === r.id}
            />
          ))}
        </div>
      )}

      <InlineLightbox url={lightbox} alt="故事板" onClose={() => setLightbox(null)} />
    </section>
  );
}

function StoryboardPendingCard({
  panelCount,
  aspectRatio,
  hasContinuityPanel,
}: {
  panelCount: number;
  aspectRatio: '9:16' | '16:9';
  hasContinuityPanel: boolean;
}) {
  const n = Math.max(1, panelCount || 1);
  return (
    <div className="overflow-hidden rounded-md border border-brand-200 bg-surface">
      <style>{`
        @keyframes sf-sb-grad {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        className="grid max-h-41 gap-px overflow-hidden bg-outline-variant/20"
        style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: n }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-center"
            style={{
              aspectRatio: aspectRatio.replace(':', '/'),
              background:
                'linear-gradient(135deg, #fdf2f8 0%, #ede9fe 25%, #dbeafe 50%, #ccfbf1 75%, #fef9c3 100%)',
              backgroundSize: '300% 300%',
              animation: 'sf-sb-grad 6s ease-in-out infinite',
            }}
          >
            <span className="text-[9px] text-brand-600">
              {hasContinuityPanel && i === 0 ? '衔接' : `镜${i + (hasContinuityPanel ? 0 : 1)}`}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-1.5 border-t border-outline-variant/20 bg-surface px-2 py-1.5 text-[11px] text-brand-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        故事板渲染中… · {n} 格黑白线稿
      </div>
    </div>
  );
}

function StoryboardCard({
  run,
  labels,
  hasContinuityPanel,
  aspectRatio,
  selected,
  onOpen,
  onSelect,
  onDeselect,
  disabled,
  armed,
  onArmDelete,
  onConfirmDelete,
  deleting,
}: {
  run: SceneStoryboardRun;
  labels: ShotLabel[];
  hasContinuityPanel: boolean;
  aspectRatio: '9:16' | '16:9';
  selected: boolean;
  onOpen: () => void;
  onSelect: () => void;
  onDeselect: () => void;
  disabled?: boolean;
  armed: boolean;
  onArmDelete: () => void;
  onConfirmDelete: () => void;
  deleting?: boolean;
}) {
  const n = run.panelCount || labels.length || 1;
  const labelOffset = hasContinuityPanel ? 1 : 0;
  return (
    <div
      className={`overflow-hidden rounded-md border bg-surface transition ${
        selected ? 'border-brand-500 ring-2 ring-brand-300' : 'border-outline-variant/30'
      }`}
    >
      {labels.length > 0 && (
        <div
          className="grid gap-px border-b border-outline-variant/20 bg-outline-variant/20"
          style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: n }).map((_, i) => {
            const lb = labels[i - labelOffset];
            const meta = [lb?.shotSize, lb?.cameraMove].filter(Boolean).join(' · ');
            return (
              <div key={i} className="bg-white px-1.5 py-1 text-[9px] leading-tight">
                <div className="font-semibold text-neutral-800">
                  {hasContinuityPanel && i === 0 ? '衔接' : `镜${lb?.no ?? i + 1 - labelOffset}`}
                  {lb?.duration ? ` · ${lb.duration}` : ''}
                </div>
                {meta && <div className="mt-px truncate text-neutral-500" title={meta}>{meta}</div>}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onOpen}
        className="block w-full cursor-zoom-in bg-neutral-900"
        title="点击放大查看"
      >
        <img
          src={run.url}
          alt="故事板"
          className="mx-auto block max-h-40 w-full object-contain"
        />
      </button>

      {labels.length > 0 && labels.some((l) => l.dialogue || l.sfx) && (
        <div
          className="grid gap-px border-t border-outline-variant/20 bg-outline-variant/20"
          style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: n }).map((_, i) => {
            const lb = labels[i - labelOffset];
            return (
              <div key={i} className="bg-white px-1.5 py-1 text-[9px] leading-tight">
                <div className="text-sky-700" title="台词">
                  {hasContinuityPanel && i === 0 ? '上一场结束帧' : `💬 ${lb?.dialogue || '无'}`}
                </div>
                {lb?.sfx && (
                  <div className="text-amber-700" title="音效">
                    🔊 {lb.sfx}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-1 border-t border-outline-variant/20 bg-surface px-2 py-1 text-[10px] text-neutral-500">
        <span>
          黑白线稿 · {n} 列 · {aspectRatio}
        </span>
        <div className="flex items-center gap-1">
          {run.url &&
            (selected ? (
              <button
                type="button"
                onClick={onDeselect}
                disabled={disabled}
                className={`inline-flex items-center gap-0.5 rounded border border-brand-300 bg-brand-50 px-1.5 py-0.5 text-brand-700 transition hover:bg-brand-100 ${
                  disabled ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <X className="h-2.5 w-2.5" />
                取消选定
              </button>
            ) : (
              <button
                type="button"
                onClick={onSelect}
                disabled={disabled}
                className={`inline-flex items-center gap-0.5 rounded border border-outline-variant/40 px-1.5 py-0.5 text-neutral-600 transition hover:bg-brand-50 ${
                  disabled ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <Check className="h-2.5 w-2.5" />
                设为本场景
              </button>
            ))}
          <button
            type="button"
            onClick={() => {
              if (armed) onConfirmDelete();
              else onArmDelete();
            }}
            disabled={deleting}
            className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-white transition disabled:opacity-50 ${
              armed ? 'animate-pulse bg-rose-600 ring-1 ring-rose-300' : 'bg-rose-500/80 hover:bg-rose-600'
            }`}
            title={armed ? '再点确认删除' : '删除'}
          >
            {deleting ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Trash2 className="h-2.5 w-2.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
